import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Rating,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
  PlayArrow as TestIcon,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Error,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { sourcesAPI, sourceConfigsAPI } from '../../../services/leadGenAPI';

const LeadSourcesList = () => {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    setLoading(true);
    try {
      const response = await sourcesAPI.getSources();
      setSources(response.results || response);
    } catch (error) {
      console.error('Error fetching sources:', error);
      setSources([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (sourceId, newStatus) => {
    try {
      // TODO: API call to update source status
      setSources(prev => prev.map(source =>
        source.id === sourceId ? { ...source, status: newStatus } : source
      ));
    } catch (error) {
      console.error('Error updating source status:', error);
    }
  };

  const handleTestConnection = async (source) => {
    setSelectedSource(source);
    setTestDialogOpen(true);
    setTestResult(null);

    try {
      const response = await sourceConfigsAPI.testConnection(source.id);
      setTestResult({
        success: true,
        message: 'Connection successful! All endpoints are responding correctly.',
        details: response
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Connection failed. Please check your configuration.',
        details: {
          error: error.message,
          suggestions: ['Verify API credentials', 'Check network connectivity', 'Review endpoint URLs']
        }
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'testing': return 'warning';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  const getConfigStatusColor = (status) => {
    switch (status) {
      case 'configured': return 'success';
      case 'needs_config': return 'error';
      case 'testing': return 'warning';
      default: return 'default';
    }
  };

  const getConfigStatusIcon = (status) => {
    switch (status) {
      case 'configured': return <CheckCircle />;
      case 'needs_config': return <Error />;
      case 'testing': return <Warning />;
      default: return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getQualityScoreColor = (score) => {
    if (score >= 4.0) return 'success';
    if (score >= 3.0) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" color="text.primary">
          Lead Sources
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/lead-gen/sources/create')}
        >
          Add Source
        </Button>
      </Box>

      {/* Performance Overview */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Sources
              </Typography>
              <Typography variant="h4" component="div">
                {sources.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {sources.filter(s => s.status === 'active').length} active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Avg Quality Score
              </Typography>
              <Typography variant="h4" component="div">
                {(sources.reduce((sum, s) => sum + s.qualityScore, 0) / sources.length).toFixed(1)}
              </Typography>
              <Rating 
                value={sources.reduce((sum, s) => sum + s.qualityScore, 0) / sources.length} 
                readOnly 
                size="small"
                precision={0.1}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Avg Conversion Rate
              </Typography>
              <Typography variant="h4" component="div">
                {(sources.reduce((sum, s) => sum + s.conversionRate, 0) / sources.length).toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Across all sources
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Leads This Month
              </Typography>
              <Typography variant="h4" component="div">
                {sources.reduce((sum, s) => sum + s.thisMonth, 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                From all sources
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sources Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Source Name & Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Quality Score</TableCell>
                <TableCell>Performance</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Configuration</TableCell>
                <TableCell>Last Used</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : sources.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No lead sources found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sources
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((source) => (
                    <TableRow key={source.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">{source.name}</Typography>
                          <Chip label={source.type} size="small" variant="outlined" />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={source.status}
                            color={getStatusColor(source.status)}
                            size="small"
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={source.status === 'active'}
                                onChange={(e) => handleStatusChange(source.id, e.target.checked ? 'active' : 'inactive')}
                              />
                            }
                            label=""
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Rating 
                            value={source.quality_score} 
                            readOnly 
                            size="small"
                            precision={0.1}
                          />
                          <Typography variant="body2" color={getQualityScoreColor(source.quality_score)}>
                            {source.quality_score}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {source.conversion_rate}% conversion
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {source.this_month} leads this month
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatCurrency(source.cost_per_lead)} per lead
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            icon={getConfigStatusIcon(source.config_status)}
                            label={source.config_status.replace('_', ' ')}
                            color={getConfigStatusColor(source.config_status)}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {formatDate(source.last_used)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/lead-gen/sources/${source.id}`)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Configure">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/lead-gen/sources/${source.id}/configure`)}
                            >
                              <SettingsIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Test Connection">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleTestConnection(source)}
                            >
                              <TestIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Source">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/lead-gen/sources/${source.id}/edit`)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={sources.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Test Connection Dialog */}
      <Dialog 
        open={testDialogOpen} 
        onClose={() => setTestDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Test Connection - {selectedSource?.name}
        </DialogTitle>
        <DialogContent>
          {!testResult ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <Typography>Testing connection...</Typography>
            </Box>
          ) : (
            <Box>
              <Alert 
                severity={testResult.success ? 'success' : 'error'}
                sx={{ mb: 2 }}
              >
                {testResult.message}
              </Alert>
              
              {testResult.details && (
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom color="text.primary">
                      Test Details
                    </Typography>
                    {testResult.success ? (
                      <Box>
                        <Typography variant="body2">
                          <strong>Response Time:</strong> {testResult.details.responseTime}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Last Sync:</strong> {formatDate(testResult.details.lastSync)}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Tested Endpoints:</strong>
                        </Typography>
                        <Box component="ul" sx={{ pl: 2 }}>
                          {testResult.details.endpoints.map((endpoint, index) => (
                            <Typography key={index} component="li" variant="body2">
                              {endpoint}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2" color="error" gutterBottom>
                          <strong>Error:</strong> {testResult.details.error}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Suggestions:</strong>
                        </Typography>
                        <Box component="ul" sx={{ pl: 2 }}>
                          {testResult.details.suggestions.map((suggestion, index) => (
                            <Typography key={index} component="li" variant="body2">
                              {suggestion}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>
            Close
          </Button>
          {testResult && !testResult.success && (
            <Button 
              variant="contained"
              onClick={() => navigate(`/lead-gen/sources/${selectedSource?.id}/configure`)}
            >
              Configure Source
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadSourcesList; 